import {Field, Formik, FormikHelpers as FormikActions} from 'formik';
import React from 'react';
import {Button} from '~/components/Button';
import {FileField, Form, FormColumn, FormControls, FormRow, FormTitle, hasErrors, InputField, IntentText} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {SelectCountryField} from '~/components/SelectCountry';
import {TabsWithBody} from '~/components/Tabs';
import {Intent} from '~/lib/intent';
import {IPaymentDetails, PaymentDetailsSchema} from '~/modules/Clients/models/PaymentDetails';

interface IPaymentDetailsFormProps {
    paymentDetails?: IPaymentDetails;
    readonly?: boolean;
    onSubmit?: (values: IPaymentDetails, formikActions: FormikActions<IPaymentDetails>, close: IModalComponentClose) => void;
}

const defaultPaymentDetails: IPaymentDetails = {
    sellerId: 0,
    taxFrom: undefined,
    beneficiaryName: '',
    beneficiaryCountry: '',
    beneficiaryState: '',
    beneficiaryCity: '',
    beneficiaryStreetAddress1: '',
    beneficiaryStreetAddress2: '',
    beneficiaryZipCode: '',
    beneficiaryPhoneNumber: '',
    bankCountry: '',
    bankName: '',
    beneficiaryAccountName: '',
    bankAccountNumber: '',
    bankSWIFTCode: '',
    intermediaryBankName: '',
    intermediaryBankAccountNumber: '',
    intermediaryBankSWIFTCode: '',
};

export const PaymentDetailsForm: IModalComponent<IPaymentDetailsFormProps> = props =>
    <Formik initialValues={props.paymentDetails || defaultPaymentDetails}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={PaymentDetailsSchema}
        onSubmit={(paymentDetails, actions) => props.onSubmit(paymentDetails, actions, props.close)}
    >
        {formik => {
            const disabled = props.readonly || formik.isSubmitting;
            const { status } = formik;

            const tabs = [
                {
                    title: 'Beneficiary',
                    intent: hasErrors(formik, [
                        'beneficiaryName',
                        'beneficiaryCountry',
                        'beneficiaryState',
                        'beneficiaryCity',
                        'beneficiaryStreetAddress1',
                        'beneficiaryStreetAddress2',
                        'beneficiaryZipCode',
                        'beneficiaryPhoneNumber'
                    ]) && Intent.DANGER,
                    children: (
                        <FormRow noBorder>
                            <FormColumn>
                                <Field
                                    name='beneficiaryName'
                                    label='Name'
                                    options={{
                                        placeholder: 'name',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryCountry'
                                    label='Beneficiary country'
                                    options={{
                                        placeholder: 'country',
                                        disabled,
                                        searchable: true
                                    }}
                                    component={SelectCountryField}
                                />
                                <Field
                                    name='beneficiaryState'
                                    label='State'
                                    options={{
                                        placeholder: 'state',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryCity'
                                    label='City'
                                    options={{
                                        placeholder: 'city',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryStreetAddress1'
                                    label='Street address 1'
                                    options={{
                                        placeholder: 'address',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryStreetAddress2'
                                    label='Street address 2'
                                    options={{
                                        placeholder: 'address',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryZipCode'
                                    label='ZIP code'
                                    options={{
                                        placeholder: 'zip code',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryPhoneNumber'
                                    label='Phone Number'
                                    options={{
                                        placeholder: 'phone number',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                            </FormColumn>
                        </FormRow>
                    )
                },
                {
                    title: 'Bank',
                    intent: hasErrors(formik, [
                        'bankName',
                        'beneficiaryAccountName',
                        'bankAccountNumber',
                        'bankCountry',
                        'bankSWIFTCode',
                    ]) && Intent.DANGER,
                    children: (
                        <FormRow noBorder>
                            <FormColumn>
                                <Field
                                    name='bankName'
                                    label='Bank Name'
                                    options={{
                                        placeholder: 'bank name',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='beneficiaryAccountName'
                                    label='Beneficiary Account Name'
                                    options={{
                                        placeholder: 'account name',
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='bankAccountNumber'
                                    label='IBAN or Bank Account number'
                                    options={{
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='bankCountry'
                                    label='Bank Country'
                                    options={{
                                        disabled,
                                        searchable: true
                                    }}
                                    component={SelectCountryField}
                                />
                                <Field
                                    name='bankSWIFTCode'
                                    label='Bank SWIFT Code'
                                    options={{
                                        disabled
                                    }}
                                    component={InputField}
                                />
                            </FormColumn>
                        </FormRow>
                    )
                },
                {
                    title: 'Intermediary bank information',
                    intent: hasErrors(formik, [
                        'intermediaryBankName',
                        'intermediaryBankAccountNumber',
                        'intermediaryBankSWIFTCode',
                    ]) && Intent.DANGER,
                    children: (
                        <FormRow noBorder>
                            <FormColumn>
                                <Field
                                    name='intermediaryBankName'
                                    label='Intermediary bank name'
                                    options={{
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='intermediaryBankAccountNumber'
                                    label='Intermediary account number'
                                    options={{
                                        disabled
                                    }}
                                    component={InputField}
                                />
                                <Field
                                    name='intermediaryBankSWIFTCode'
                                    label='Intermediary Bank SWIFT Code'
                                    options={{
                                        disabled
                                    }}
                                    component={InputField}
                                />
                            </FormColumn>
                        </FormRow>
                    )
                }
            ];

            return <Form>
                <FormTitle>{!props.readonly && 'Edit'} Payment Details</FormTitle>
                {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                <TabsWithBody tabs={tabs} />
                <FormRow>
                    <FormColumn>
                        <Field name='taxForm'
                            label='Tax Form'
                            options={{
                                disabled,
                            }}
                            note={<>
                                   It's <b>mandatory</b> to upload&nbsp;
                                <a href='http://www.irs.gov/pub/irs-pdf/fw9.pdf' target='_blank'>W9</a>,&nbsp;
                                <a href='http://www.irs.gov/pub/irs-pdf/fw8ben.pdf' target='_blank'>W-8BEN</a> or&nbsp;
                                <a href='https://www.irs.gov/pub/irs-pdf/fw8bene.pdf' target='_blank'>W-8BEN-E</a> tax form.
                            </>}
                            component={FileField}
                        />
                    </FormColumn>
                </FormRow>
                <FormControls>
                    <Button disabled={formik.isSubmitting}
                        onClick={props.close}
                    >Cancel</Button>
                    {!props.readonly && <Button type='submit'
                        disabled={formik.isSubmitting}
                        intent={Intent.PRIMARY}
                        rightIcon={formik.isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                    >Save</Button>}
                </FormControls>
            </Form>;
        }}
    </Formik>;
